import { Card, CardButtonActionMode, HeaderGradient, MiniCard, Row } from '@app/components';
import React, { memo } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { StyleSheet } from 'react-native';
import { ITax } from '@app/common';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { SlantTitle } from '@app/components/typography';
import SvgStats from '@app/components/icons/SvgStats';
import * as R from 'remeda';
import { Button } from '@app/components/form/buttons';

type TaxCardProps = {
  tax?: Partial<ITax>;
  toggle: (mode: CardButtonActionMode) => void;
};

const formatCardData = (partialTax: Partial<ITax>) => {
  return {
    name: partialTax?.name || ''
  }
}

export const TaxCard = memo(({ tax, toggle }: TaxCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const partialTax = useAppSelector<Partial<ITax>>(triptychSelectors.selectData);
  const cardData = useAppSelector<Partial<ITax>>(triptychSelectors.selectCardData);
  const mode = useAppSelector(triptychSelectors.selectMode);
  const isOpen = useAppSelector(triptychSelectors.selectIsOpen);

  const { name } = formatCardData(cardData ?? partialTax);

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Fiche taxe</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            {/* En-tête */}
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>{name}</Card.Title>
                {tax?._id && <Card.Subtitle>Ref: {tax._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              {/* Afficher le bouton seulement si ce n'est pas en mode CREATE */}
              {mode === 'UPDATE' && (
                <Button
                  title={isOpen ? "Fermer" : "Editer"}
                  bordered={isOpen}
                  onPress={() => toggle(isOpen ? "CLOSE" : "UPDATE")} />
              )}
            </Row>
          </Card.Header>
        </Card.Content>

        {!R.isEmptyish(tax) && (
          <MiniCard>
            <MiniCard.Tabs>
              <MiniCard.TabsHolder>
                <MiniCard.Tab Icon={SvgStats} isActive />
              </MiniCard.TabsHolder>
            </MiniCard.Tabs>
            <MiniCard.Container>
              <MiniCard.Content>

                <Card.LineBreak height={16} />

                <SlantTitle label="Informations" color={theme.colors.gold} />

                <Card.HelpScrollContainer extraStyle={{ paddingLeft: 24, paddingRight: 24, paddingTop: 60 }}>

                  {/* Informations générales */}
                  <InfoSection>
                    <SectionTitle>📊 Informations générales</SectionTitle>
                    <InfoGrid>
                      {tax.name && (
                        <InfoRow>
                          <InfoLabel>Nom</InfoLabel>
                          <InfoValue>{tax.name}</InfoValue>
                        </InfoRow>
                      )}
                      {tax.codeVAT !== undefined && (
                        <InfoRow>
                          <InfoLabel>Code VAT</InfoLabel>
                          <InfoValue>{tax.codeVAT}</InfoValue>
                        </InfoRow>
                      )}
                      {tax.nameVAT && (
                        <InfoRow>
                          <InfoLabel>Nom VAT</InfoLabel>
                          <InfoValue>{tax.nameVAT}</InfoValue>
                        </InfoRow>
                      )}
                      {tax.rate !== undefined && (
                        <InfoRow>
                          <InfoLabel>Taux</InfoLabel>
                          <InfoValue>{tax.rate}%</InfoValue>
                        </InfoRow>
                      )}
                      {tax.salesCodeHT !== undefined && (
                        <InfoRow>
                          <InfoLabel>Code vente HT</InfoLabel>
                          <InfoValue>{tax.salesCodeHT}</InfoValue>
                        </InfoRow>
                      )}
                      {tax.salesNameHT && (
                        <InfoRow>
                          <InfoLabel>Nom vente HT</InfoLabel>
                          <InfoValue>{tax.salesNameHT}</InfoValue>
                        </InfoRow>
                      )}
                    </InfoGrid>
                  </InfoSection>

                  <Card.LineBreak height={24} />

                </Card.HelpScrollContainer>
              </MiniCard.Content>
            </MiniCard.Container>
          </MiniCard>
        )}
      </Card.Container>
    </Card>
  );
});


const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});

// Styled Components
const InfoSection = styled.View({
  marginBottom: normalize(28),
});

const SectionTitle = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(18),
  fontWeight: 'bold',
  marginBottom: normalize(16),
  letterSpacing: 0.3,
});

const InfoGrid = styled.View({
  gap: normalize(12),
  backgroundColor: 'rgba(253, 255, 252, 0.03)',
  borderRadius: normalize(12),
  padding: normalize(16),
  borderWidth: 1,
  borderColor: 'rgba(253, 255, 252, 0.08)',
});

const InfoRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: normalize(8),
});

const InfoLabel = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(14),
  fontWeight: '500',
  flex: 1,
});

const InfoValue = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(15),
  fontWeight: '600',
  flex: 1,
  textAlign: 'right',
});

